import { Chart } from 'react-chartjs-2';

import {
  AnimationEvent,
  BarController,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  ScriptableScaleContext,
  SubTitle,
  Title,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getColorStyles } from 'PDF/helpers/documentStyle';
import { checkIsEmptyDataByKey } from 'PDF/helpers/pdfMapping';

import { formatNumber } from 'helpers/numbers';

import { emptyChart, ILinesChart } from '../types';

ChartJS.register(
  LinearScale,
  CategoryScale,
  PointElement,
  LineElement,
  Legend,
  BarController,
  Title,
  SubTitle,
  LineController,
);

export type ILinesChartProps = {
  handleOnComplete: (uri: string) => void;
  info: ILinesChart;
  aspectRatio?: number;
};

ChartJS.defaults.font.family = 'Noto Sans';

export const LinesChart = ({
  info: { labels, datasets, colors, withDot = true, yAxisBold = false, valueFormatter },
  aspectRatio = 2,
  handleOnComplete,
}: ILinesChartProps) => {
  const defaultColors = getColorStyles([14, 16, 7, 12]);

  const chartData = {
    labels,
    datasets: datasets.map(({ label, data }, index) => ({
      label,
      data,
      borderColor: colors?.[index] ?? defaultColors[index],
      borderWidth: 1,
      pointRadius: withDot ? 3 : 0,
      backgroundColor: colors?.[index] ?? defaultColors[index],
      pointBorderColor: colors?.[index] ?? defaultColors[index],
      fill: false,
    })),
  };
  const options: ChartOptions = {
    aspectRatio,
    devicePixelRatio: 1,
    layout: { padding: { top: 40 } },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center',
        labels: {
          boxWidth: 26,
          boxHeight: 2,
          padding: 30,
          font: {
            size: 26,
            weight: 300,
          },
          pointStyle: 'line',
          pointStyleWidth: 6,
        },
      },
      datalabels: {
        display: 'auto',
        anchor: 'end',
        align: 'end',
        font: {
          size: 26,
          weight: 400,
        },
        color: (ctx: any) => ctx.dataset.borderColor,
        formatter: (value: string) =>
          valueFormatter ? valueFormatter(formatNumber(+value, 1)) : formatNumber(+value, 1),
      },
    },
    scales: {
      y: {
        display: true,
        ticks: {
          color: '#77787C',
          font: {
            size: 26,
            weight: yAxisBold ? 700 : 300,
          },
        },
        grid: {
          color: (ctx: ScriptableScaleContext) => (ctx.tick.value === 0 ? '#77787C' : 'transparent'),
          tickBorderDash: [3, 7],
        },
        border: { display: false, dash: [3, 7] },
      },
      x: {
        display: true,
        offset: true,
        ticks: {
          font: {
            size: 26,
            weight: 300,
          },
          color: '#77787C',
        },
        grid: {
          color: 'transparent',
        },
        border: { display: true, color: 'transparent' },
      },
    },
    animation: {
      duration: 0,
      onComplete(event: AnimationEvent) {
        handleOnComplete(checkIsEmptyDataByKey(datasets, 'data') ? emptyChart : event.chart.toBase64Image());
      },
    },
  };

  return <Chart type="line" data={chartData} options={options} plugins={[ChartDataLabels]} />;
};
